import React from 'react';
import { Sparkles, Check, Clock, UserCircle } from 'lucide-react';
import type { MessageItem } from '@/types';
import { cn, formatMessageTimestamp } from '@/lib/utils';
import { useAuthStore } from '@/stores/useAuthStore';

interface MessageBubbleProps {
  message: MessageItem;
  customerName?: string | null;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, customerName }) => {
  const { user } = useAuthStore();
  const senderStr = String(message.senderType).toUpperCase();
  const rawSender = message.senderType as any;

  // Defensively hide internal AI tool calls, tool results, and empty messages
  const isTool = senderStr === 'TOOL' || rawSender === 4;
  const isEvent = senderStr === 'EVENT' || rawSender === 5;
  const hasNoContent = !message.content || message.content.trim() === '';

  if (isTool || isEvent || (hasNoContent && !message.isPending)) {
    return null;
  }

  // Determine sender role
  const isAI = senderStr === 'AI' || message.senderType === 1;
  const isAgent = senderStr === 'HUMANAGENT' || senderStr === 'AGENT' || message.senderType === 2;
  const isSystem = senderStr === 'SYSTEM' || message.senderType === 3;

  const rawTime =
    message.createdAtUtc ||
    message.createdAt ||
    message.timestamp ||
    (message as any).created_at;

  const timeDisplay = formatMessageTimestamp(rawTime);
  const fullTimeTitle = rawTime && !isNaN(new Date(rawTime).getTime())
    ? new Date(rawTime).toLocaleString('pt-BR')
    : undefined;

  // 1. System Events
  if (isSystem) {
    return (
      <div className="flex justify-center my-3">
        <div className="bg-muted/60 text-muted-foreground text-[11px] font-medium px-3 py-1 rounded-full border border-border/40 text-center max-w-[90%] shadow-xs">
          <span>{message.content}</span>
          {timeDisplay && (
            <span className="ml-1.5 opacity-70" title={fullTimeTitle}>
              ({timeDisplay})
            </span>
          )}
        </div>
      </div>
    );
  }

  // 2. Human Live Agent
  if (isAgent) {
    const isMe = !message.senderId || message.senderId === user?.id || message.isPending;

    if (isMe) {
      // Right-aligned, primary bubble
      return (
        <div className="flex flex-col items-end my-2 space-y-1">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mr-1">
            <span className="font-medium">Você</span>
            {timeDisplay && (
              <>
                <span>•</span>
                <span title={fullTimeTitle}>{timeDisplay}</span>
              </>
            )}
            {message.isPending ? (
              <Clock className="h-2.5 w-2.5 animate-spin text-muted-foreground" />
            ) : (
              <Check className="h-3 w-3 text-primary" />
            )}
          </div>
          <div
            className={cn(
              'bg-primary text-primary-foreground rounded-2xl rounded-tr-xs px-4 py-2.5 max-w-[85%] sm:max-w-[75%] shadow-sm',
              message.isPending && 'opacity-70'
            )}
          >
            <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words">
              {message.content}
            </p>
          </div>
        </div>
      );
    } else {
      // Left-aligned, different style for other agents
      return (
        <div className="flex flex-col items-start my-2 space-y-1">
          <div className="flex items-center gap-1.5 text-[11px] text-blue-700 dark:text-blue-300 ml-1">
            <UserCircle className="h-3 w-3" />
            <span className="font-medium">{message.guestName || message.senderName || 'Atendente'}</span>
            {timeDisplay && (
              <>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground" title={fullTimeTitle}>{timeDisplay}</span>
              </>
            )}
          </div>
          <div className="bg-blue-50 dark:bg-blue-950/40 text-foreground border border-blue-200/80 dark:border-blue-800/80 rounded-2xl rounded-tl-xs px-4 py-2.5 max-w-[85%] sm:max-w-[75%] shadow-xs">
            <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words">
              {message.content}
            </p>
          </div>
        </div>
      );
    }
  }

  // 3. AI Assistant (Left-aligned, violet tinted bubble with bot icon)
  if (isAI) {
    return (
      <div className="flex flex-col items-start my-2 space-y-1">
        <div className="flex items-center gap-1.5 text-[11px] text-violet-700 dark:text-violet-300 ml-1">
          <Sparkles className="h-3 w-3" />
          <span className="font-medium">Assistente IA</span>
          {timeDisplay && (
            <>
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground" title={fullTimeTitle}>{timeDisplay}</span>
            </>
          )}
        </div>
        <div className="bg-violet-50 dark:bg-violet-950/40 text-foreground border border-violet-200/80 dark:border-violet-800/80 rounded-2xl rounded-tl-xs px-4 py-2.5 max-w-[85%] sm:max-w-[75%] shadow-xs">
          <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words">
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  // 4. Customer / Client (Left-aligned, neutral bubble)
  return (
    <div className="flex flex-col items-start my-2 space-y-1">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground ml-1">
        <span className="font-medium text-foreground">
          {message.guestName || message.senderName || customerName || 'Usuário Anônimo'}
        </span>
        {timeDisplay && (
          <>
            <span>•</span>
            <span title={fullTimeTitle}>{timeDisplay}</span>
          </>
        )}
      </div>
      <div className="bg-muted text-foreground border border-border/40 rounded-2xl rounded-tl-xs px-4 py-2.5 max-w-[85%] sm:max-w-[75%] shadow-xs">
        <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words">
          {message.content}
        </p>
      </div>
    </div>
  );
};

export default MessageBubble;
